export class PerformanceMonitor {
    constructor() {
        this.metrics = {
            updateCount: 0,
            totalUpdateTime: 0,
            maxUpdateTime: 0,
            minUpdateTime: Infinity,
            lastReportTime: Date.now(),
            frameDrops: 0,
            lastFrameTime: performance.now()
        };
        
        this.isMonitoring = false;
        this.reportInterval = 5000; // Report every 5 seconds
        this.frameThreshold = 16.67; // 60fps threshold
    }

    startMonitoring() {
        if (this.isMonitoring) return;
        
        this.isMonitoring = true;
        this.resetMetrics();
        
        // Monitor frame drops using requestAnimationFrame
        this.monitorFrameRate();
        
        // Start periodic reporting
        this.reportIntervalId = setInterval(() => {
            this.report();
        }, this.reportInterval);
        
        console.log('%c🔍 Performance monitoring started', 'color: #4CAF50; font-weight: bold');
    }

    stopMonitoring() {
        this.isMonitoring = false;
        
        if (this.reportIntervalId) {
            clearInterval(this.reportIntervalId);
        }
        
        if (this.frameMonitorId) {
            cancelAnimationFrame(this.frameMonitorId);
        }
        
        this.report();
        console.log('%c🛑 Performance monitoring stopped', 'color: #f44336; font-weight: bold');
    }

    measureUpdate(updateFn) {
        if (!this.isMonitoring) return updateFn();
        
        const startTime = performance.now();
        const result = updateFn();
        const endTime = performance.now();
        
        const duration = endTime - startTime;
        
        this.metrics.updateCount++;
        this.metrics.totalUpdateTime += duration;
        this.metrics.maxUpdateTime = Math.max(this.metrics.maxUpdateTime, duration);
        this.metrics.minUpdateTime = Math.min(this.metrics.minUpdateTime, duration);
        
        // Warn if update takes too long
        if (duration > 16.67) { // More than one frame (60fps)
            console.warn(`%c⚠️ Slow update detected: ${duration.toFixed(2)}ms`, 'color: #ff9800');
        }
        
        return result;
    }

    monitorFrameRate() {
        if (!this.isMonitoring) return;
        
        const currentFrameTime = performance.now();
        const frameDelta = currentFrameTime - this.metrics.lastFrameTime;
        
        // Check if frame took longer than threshold (dropped frame)
        if (frameDelta > this.frameThreshold * 2) {
            this.metrics.frameDrops++;
        }
        
        this.metrics.lastFrameTime = currentFrameTime;
        
        this.frameMonitorId = requestAnimationFrame(() => {
            this.monitorFrameRate();
        });
    }

    getCPUMetrics() {
        // Use Performance Observer API if available
        if (window.PerformanceObserver && PerformanceObserver.supportedEntryTypes?.includes('measure')) {
            const entries = performance.getEntriesByType('measure');
            return entries.slice(-10); // Get last 10 measures
        }
        return [];
    }

    getMemoryUsage() {
        // Chrome-specific memory API
        if (performance.memory) {
            return {
                usedJSHeapSize: (performance.memory.usedJSHeapSize / 1048576).toFixed(2) + ' MB',
                totalJSHeapSize: (performance.memory.totalJSHeapSize / 1048576).toFixed(2) + ' MB',
                jsHeapSizeLimit: (performance.memory.jsHeapSizeLimit / 1048576).toFixed(2) + ' MB'
            };
        }
        return null;
    }

    report() {
        if (this.metrics.updateCount === 0) {
            console.log('%c📊 No SSE updates in this period', 'color: #9e9e9e');
            return;
        }

        const avgUpdateTime = this.metrics.totalUpdateTime / this.metrics.updateCount;
        const updatesPerSecond = (this.metrics.updateCount / (this.reportInterval / 1000)).toFixed(2);
        
        console.group('%c📊 SSE Performance Report', 'color: #2196F3; font-weight: bold');
        console.table({
            'Updates': this.metrics.updateCount,
            'Updates/sec': updatesPerSecond,
            'Avg Update Time': avgUpdateTime.toFixed(2) + 'ms',
            'Max Update Time': this.metrics.maxUpdateTime.toFixed(2) + 'ms',
            'Min Update Time': this.metrics.minUpdateTime.toFixed(2) + 'ms',
            'Frame Drops': this.metrics.frameDrops,
            'CPU Time': (this.metrics.totalUpdateTime / this.reportInterval * 100).toFixed(2) + '%'
        });
        
        const memory = this.getMemoryUsage();
        if (memory) {
            console.log('%cMemory Usage:', 'color: #FF9800; font-weight: bold', memory);
        }
        
        // Performance warnings
        if (avgUpdateTime > 10) {
            console.warn('%c⚠️ High average update time detected', 'color: #ff5722');
        }
        
        if (this.metrics.frameDrops > 10) {
            console.warn(`%c⚠️ ${this.metrics.frameDrops} frame drops detected`, 'color: #ff5722');
        }
        
        console.groupEnd();
        
        // Reset metrics for next period
        this.resetMetrics();
    }

    resetMetrics() {
        this.metrics.updateCount = 0;
        this.metrics.totalUpdateTime = 0;
        this.metrics.maxUpdateTime = 0;
        this.metrics.minUpdateTime = Infinity;
        this.metrics.frameDrops = 0;
        this.metrics.lastReportTime = Date.now();
    }

    // Utility to measure render performance
    measureRender(componentName, renderFn) {
        if (!this.isMonitoring) return renderFn();
        
        performance.mark(`${componentName}-start`);
        const result = renderFn();
        performance.mark(`${componentName}-end`);
        performance.measure(componentName, `${componentName}-start`, `${componentName}-end`);
        
        return result;
    }
}

// Create singleton instance
export const perfMonitor = new PerformanceMonitor();

// Browser DevTools integration
if (typeof window !== 'undefined') {
    window.ssePerformance = perfMonitor;
    console.log('%c💡 SSE Performance Monitor available as window.ssePerformance', 'color: #4CAF50');
    console.log('%c   Start monitoring: window.ssePerformance.startMonitoring()', 'color: #9e9e9e');
    console.log('%c   Stop monitoring: window.ssePerformance.stopMonitoring()', 'color: #9e9e9e');
}